import type { RenderedComponent } from "./scan-rendered-components.types";
import {
  componentMap,
  extractDebugSourceFile,
  isInternalFile,
  resolveComponentType,
  resolveManifestPath,
  SKIP_COMPONENT_NAMES,
} from "./tab-source";

export type { RenderedComponent };

// Walk the React fiber tree from document.body to collect all rendered
// components that appear in the component manifest. Runs on-demand.
export function scanRenderedComponents(pathname: string): RenderedComponent[] {
  if (process.env.NODE_ENV !== "development") return [];

  const bodyEl = document.body;
  const fiberKey = Object.keys(bodyEl).find((k) =>
    k.startsWith("__reactFiber$"),
  );
  if (!fiberKey) return [];

  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-member-access -- React internal fiber API
  const rootFiber: any = (bodyEl as any)[fiberKey];
  if (!rootFiber) return [];

  const found = new Map<string, RenderedComponent>();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- fiber tree walk
  const queue: any[] = [rootFiber];

  for (const child of Array.from(bodyEl.children)) {
    const childFiberKey = Object.keys(child).find((k) =>
      k.startsWith("__reactFiber$"),
    );
    if (!childFiberKey) continue;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-explicit-any -- React internal
    const raw: any = (child as any)[childFiberKey];
    if (!raw || raw === rootFiber) continue;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access -- React internal
    const childRoot = raw.current ?? raw;
    if (childRoot && childRoot !== rootFiber) queue.push(childRoot);
  }

  let visited = 0;
  const MAX = 10000;

  while (queue.length > 0 && visited < MAX) {
    visited++;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- fiber
    const fiber = queue.shift();

    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- React fiber
    const resolved = resolveComponentType(fiber.type);
    if (resolved) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- React fiber
      const outerDisplayName = fiber.type?.displayName as string | undefined;
      const name: string =
        outerDisplayName ?? resolved.displayName ?? resolved.name ?? "";
      if (name && !SKIP_COMPONENT_NAMES.has(name) && !/^[a-z]/.test(name)) {
        // Prefer the authoritative source: React's _debugSource records the
        // exact file the JSX was authored in. This sidesteps manifest
        // ambiguity when the same component name is exported from multiple
        // files (e.g. a test-* preview copy of a production component).
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-assignment -- React fiber
        const rawSource: string | undefined = fiber._debugSource?.fileName;
        const fromSource = extractDebugSourceFile(rawSource);
        const entry = componentMap[name];
        const path =
          fromSource ?? (entry ? resolveManifestPath(entry, pathname) : null);
        if (
          path &&
          !path.startsWith("components/ui/") &&
          !path.startsWith("components/debug/") &&
          !path.startsWith("providers/") &&
          !path.includes("context") &&
          !path.includes("provider") &&
          !isInternalFile(path)
        ) {
          const existing = found.get(path);
          if (existing) {
            existing.fibers.push(fiber);
          } else {
            found.set(path, { name, path, fibers: [fiber] });
          }
        }
      }
    }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- fiber tree
    if (fiber.child) queue.push(fiber.child);
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- fiber tree
    if (fiber.sibling) queue.push(fiber.sibling);
  }

  return Array.from(found.values()).sort((a, b) =>
    a.path < b.path ? -1 : a.path > b.path ? 1 : 0,
  );
}
